import { inViewChangeColour, inViewFadeUp } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { imageCoverContainer } from '@helpers/image';
import { Device, from, until } from '@helpers/media';
import { h1, h3, micro, paragraph, paragraphSmall } from '@helpers/typography';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import SimpleImage from '@stories/Components/SimpleImage/SimpleImage';
import { rgba } from 'polished';
import styled from 'styled-components';
import { BreadcrumbsStyles } from '@stories/Components/Breadcrumbs/Breadcrumbs.styles';
const Container = styled(SectionWrapper) `
  --bgColor: ${brand.grey.grey6};
  --fgColor: ${brand.white};

  position: relative;
  display: grid;
  background: var(--bgColor);
  color: var(--fgColor);
  padding-top: var(--headerHeight);

  &[data-variant='contained'][data-bgColor='darkGrey'] {
    --bgColor: ${brand.grey.grey6};
  }

  &[data-variant='contained'][data-bgColor='grey'] {
    --bgColor: ${brand.grey.grey7};
  }

  &[data-variant='contained'][data-bgColor='lightGrey'] {
    --bgColor: ${brand.grey.grey2};
  }

  &[data-variant='contained'][data-bgColor='blueGrey'] {
    --bgColor: ${brand.grey.grey5};
  }

  &[data-variant='contained'][data-bgColor='oilGas'] {
    --bgColor: ${brand.sectors.oilGasGrey};
  }

  &[data-variant='contained'][data-bgColor='marineBlue'] {
    --bgColor: ${brand.sectors.marineBlue};
  }

  &[data-variant='contained'][data-bgColor='defencePurple'] {
    --bgColor: ${brand.sectors.defencePurple};
  }

  &[data-variant='contained'][data-bgColor='energyGreen'] {
    --bgColor: ${brand.sectors.energyGreen};
  }

  &[data-variant='contained'][data-bgColor='nuclearOrange'] {
    --bgColor: ${brand.sectors.nuclearOrange};
  }

  &[data-variant='contained'][data-bgColor='transportRed'] {
    --bgColor: ${brand.sectors.transportRed};
  }

  &[data-variant='contained'][data-bgColor='white'] {
    --bgColor: ${brand.white};
    --fgColor: ${brand.black};
  }

  &[data-variant='article'] {
    --bgColor: ${brand.grey.grey7};
  }

  @media ${from(Device.TabletLarge)} {
    min-height: 92vh;

    &[data-variant='short'] {
      min-height: 55vh;
    }
  }

  &[data-variant='contained'] {
    min-height: auto;
    
    @media ${from(Device.TabletLarge)} {
      height: 650px;
    }
    @media ${from(Device.DesktopSmall)} {
      height: 750px;
    }
  }
`;
const Inner = styled.div `
  position: initial;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  @media ${until(Device.TabletLarge)} {
    padding: 48px 0;

    ${Container}[data-variant='article'] & {
      padding: 48px 0 0;
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${Container}:not([data-variant='article']) & {
      flex-direction: row;
    }
  }

  ${Container}[data-variant='article'] & {
    align-items: flex-start;
    justify-content: center;
  }

  [data-variant='contained'] & {
    @media ${from(Device.TabletLarge)} {
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
  }

  ${BreadcrumbsStyles.Container} {
    transform: translateY(-45px);

    [data-bgColor='white'] & {
      --fgColor: ${brand.black};
      --altFgColor: ${brand.black};
    }
    
    @media ${from(Device.TabletLarge)} {
      transform: translateY(0);
      position: absolute;
      top: 0;
      left: 0;
    }
  }
`;
const MediaWrapper = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  height: 100%;
  width: 100%;
  z-index: 1;

  [data-variant='contained'] & {
    position: relative;
    max-height: 800px;
    align-self: end;
    order: 1;

    @media ${until(Device.TabletLarge)} {
      display: none;
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: rgba(0, 0, 0, var(--tintAmount));
    width: 100%;

    [data-variant='contained'] & {
      content: none;
    }
  }
`;
const Image = styled(SimpleImage) `
  ${imageCoverContainer()}

  [data-variant='contained'] & {
    object-fit: contain;
    object-position: bottom right;
  }
`;
const Video = styled.video `
  ${imageCoverContainer()}
`;
const ContentWrapper = styled.div `
  position: relative;
  z-index: 2;

  [data-variant='contained'] & {
    order: 0;
  }

  @media ${from(Device.TabletLarge)} {
    align-self: center;

    ${Container}[data-variant='article'] & {
      align-self: flex-start;
    }
  }

  ${Container}[data-variant='article'] & {
    margin-top: auto;
  }
`;
const Title = styled.h1 `
  ${h1}
  margin-bottom: 12px;

  @media ${from(Device.Tablet)} {
    margin-top: 48px;
    margin-bottom: 24px;
  }

  ${inViewFadeUp({ delay: 0, duration: 0.3 })};
`;
const Subtitle = styled.div `
  ${h3};
  max-width: 770px;

  strong {
    ${inViewChangeColour({ delay: 0.15, duration: 0.3 }, `${brand.white}`, `${brand.yellow.base}`)};
  }
`;
const Content = styled.p `
  ${paragraph}
  margin-bottom: 0;
  max-width: 518px;

  ${inViewFadeUp({ delay: 0.15, duration: 0.3 })};
`;
const CtaWrapper = styled.div `
  display: block;
  margin-top: 24px;

  @media ${from(Device.Tablet)} {
    margin-top: 56px;
  }

  ${inViewFadeUp({ delay: 0.3, duration: 0.3 })};
`;
const ShareWrapper = styled.div `
  position: relative;
  align-self: center;
  border-top: 4px solid ${brand.grey.grey1};
  border-bottom: 1px solid ${rgba(brand.grey.grey1, 0.5)};
  flex: 1 1 auto;
  width: 100%;
  z-index: 2;

  @media ${from(Device.TabletLarge)} {
    margin-left: auto;
    max-width: 460px;
  }

  ${inViewFadeUp({ delay: 0.15, duration: 0.3 })};
`;
const ShareTable = styled.dl `
  display: grid;
  color: currentColor;
  ${fonts.helvetica.bold};
  font-size: 18px;
  gap: 13px 24px;
  grid-template-columns: 1fr 1fr;
  line-height: 1em;
  letter-spacing: -0.01em;
  padding: 18px 0 12px;

  @media ${from(Device.TabletLarge)} {
    padding: 32px 0 36px;
  }
`;
const ShareLabel = styled.dt ``;
const ShareValue = styled.dd `
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
const ShareTimestamp = styled.time `
  ${micro}
  display: block;
  margin: 0 0 4px;
  text-align: right;
`;
const ArticleContentWrapper = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  z-index: 1;

  @media ${from(Device.Tablet)} {
    column-gap: 30px;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;
const Logo = styled.img `
  margin-left: auto;
  max-width: ${fluid(135, 240)};
`;
const ArticleAdditionalContent = styled.div `
  position: relative;
  padding-top: 12px;
  z-index: 1;

  @media ${from(Device.MobileLarge)} {
    display: flex;
  }
`;
const FreeText = styled.p `
  ${paragraph};
  color: ${brand.white};
  opacity: 0.8;

  @media ${from(Device.MobileLarge)} {
    &:not(:last-child) {
      margin-right: 20px;

      &::after {
        content: '|';
        padding-left: 20px;
      }
    }
  }

  @media ${until(Device.MobileLarge)} {
    margin-bottom: 0;
  }
`;
const ReadArticle = styled.a `
  ${paragraphSmall};
  margin-bottom: 0;
  cursor: pointer;
  z-index: 1;

  @media ${from(Device.Desktop)} {
    margin-top: ${fluid(40, 100, Device.Desktop, Device.ActualDesktop)};
    padding-bottom: ${fluid(20, 40, Device.Desktop, Device.ActualDesktop)};
  }

  svg {
    transition: transform 0.25s ease;
    position: relative;
  }

  &:hover {
    svg {
      transform: translateY(5px);
    }
  }
`;
const ContentHeroBannerStyles = {
    ArticleAdditionalContent,
    ArticleContentWrapper,
    Container,
    Content,
    ContentWrapper,
    CtaWrapper,
    FreeText,
    Image,
    Inner,
    Logo,
    MediaWrapper,
    ReadArticle,
    ShareLabel,
    ShareTable,
    ShareTimestamp,
    ShareValue,
    ShareWrapper,
    Subtitle,
    Title,
    Video,
};
export default ContentHeroBannerStyles;
